import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getNavbarMenu } from './config';
import { useTranslation } from 'react-i18next';
import { TopArrowIcon } from '../common/icon';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '@/apis/axiosInstance';
import { Skeleton } from 'antd';
import { useTimModalStore } from '@/tim/useTimModalStore';
import { useAuth } from '@clerk/clerk-react';
import AvatarPanel from './AvatarPanel';
import { useInvitesStore } from '@/hooks/useInvitesStore';
import { useInfluencerInfo } from '@/hooks/useInfluencerInfo';
import { usePlatformStore } from '@/hooks/usePlatformStore';
import { CheckedIcon } from '../common/icon';
import ModalAfterRelease from '../web/components/ModalAfterRelease';
import { buildLoginUrl, isFromApp } from '@/biz/tool';
import DownloadAppTextButton from './DownloadAppTextButton';
import SidebarNavItem from './SidebarNavItem';

const LEADERBOARD_NEW_BADGE_FIRST_SEEN_AT = 'leaderboard_nav_new_first_seen_at';
const LEADERBOARD_NEW_BADGE_CLICKED_AT = 'leaderboard_nav_new_clicked_at';
const LEGACY_LEADERBOARD_NEW_BADGE_SEEN_AT = 'leaderboard_nav_new_seen_at';
const LEADERBOARD_NEW_BADGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export default function NavbarContent({
  setMenuOpen,
  collapsed,
  guestMode = false
}: {
  setMenuOpen?: (open: boolean) => void;
  collapsed?: boolean;
  guestMode?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = usePlatformStore();
  const { isLoaded, isSignedIn } = useAuth();
  const { influencerInfo } = useInfluencerInfo();
  const { referralInfo }: any = influencerInfo || {};
  const [openModalAfterRelease, setOpenModalAfterRelease] = useState(false);
  const { timUnreadCount, setShowTimModal } = useTimModalStore();
  const menu = useMemo(() => getNavbarMenu(t), [t]);
  const [showLeaderboardNewBadge, setShowLeaderboardNewBadge] = useState(() => {
    const now = Date.now();
    const clickedAt = Number(
      window.localStorage.getItem(LEADERBOARD_NEW_BADGE_CLICKED_AT) ||
        window.localStorage.getItem(LEGACY_LEADERBOARD_NEW_BADGE_SEEN_AT) ||
        0
    );
    if (clickedAt) return false;

    const firstSeenAt = Number(
      window.localStorage.getItem(LEADERBOARD_NEW_BADGE_FIRST_SEEN_AT) || 0
    );
    if (!firstSeenAt) {
      window.localStorage.setItem(
        LEADERBOARD_NEW_BADGE_FIRST_SEEN_AT,
        String(now)
      );
      return true;
    }
    return now - firstSeenAt < LEADERBOARD_NEW_BADGE_TTL_MS;
  });

  const stepsLists = [
    {
      title: t('components.set_onboarding_prompt.start_earning_1', {
        defaultValue: 'Platform tour'
      }),
      costTime: '10 min',
      todoKey: 'takeTheTour',
      doneState: user?.hasLearnedTheRopes
    },
    {
      title: t('components.set_onboarding_prompt.start_earning_2', {
        defaultValue: 'Deal preferences'
      }),
      costTime: '5 min',
      todoKey: 'setMyRates',
      doneState: user?.isBaselineSet
    },
    {
      title: t('components.set_onboarding_prompt.start_earning_3', {
        defaultValue: 'Contact channel'
      }),
      costTime: '1 min',
      todoKey: 'setNotifications',
      doneState: user?.isContactSet
    },
    {
      title: t('components.set_onboarding_prompt.start_earning_4', {
        defaultValue: 'Verify account'
      }),
      costTime: '1 min',
      todoKey: 'verifyNow',
      doneState: user?.isSocialConnected
    }
  ];
  const stepsDataReady = stepsLists.every(
    (step) => typeof step.doneState === 'boolean'
  );

  const toStep = (stepKey: string) => {
    const goToSettingsTab = (tab: string) => {
      if (location.pathname === '/settings') {
        window.location.hash = tab;
        return;
      }
      navigate(`/settings#${tab}`);
    };

    switch (stepKey) {
      case 'takeTheTour':
        setOpenModalAfterRelease(true);
        break;
      case 'setMyRates':
        goToSettingsTab('baselineRates');
        break;
      case 'setNotifications':
        goToSettingsTab('preferredContactInfo');
        break;
      case 'verifyNow':
        goToSettingsTab('connectSocials');
        break;
    }
  };

  const markLeaderboardSeen = () => {
    window.localStorage.setItem(
      LEADERBOARD_NEW_BADGE_CLICKED_AT,
      String(Date.now())
    );
    setShowLeaderboardNewBadge(false);
  };

  const isHrefActive = useCallback(
    (href?: string) => {
      if (!href) return false;
      const current = location.pathname;

      if (href === '/') return current === '/';
      const normalizedHref = href.endsWith('/') ? href.slice(0, -1) : href;
      if (current === normalizedHref) return true;
      return current.startsWith(`${normalizedHref}/`);
    },
    [location.pathname]
  );

  // 用于控制展开/收起的父级菜单 key（默认全部展开）
  const [openParentKeys, setOpenParentKeys] = useState<Set<string>>(() => {
    const parentKeys = menu
      .filter((item: any) => item.children?.length)
      .map((item: any) => item.key);
    return new Set(parentKeys);
  });

  // 路由命中某个子菜单时，确保对应父级至少保持展开
  useEffect(() => {
    const activeParent = menu.find((item: any) =>
      item.children?.some((child: any) => isHrefActive(child.href))
    );
    if (!activeParent?.key) return;

    setOpenParentKeys((prev) => {
      if (prev.has(activeParent.key)) return prev;
      const next = new Set(prev);
      next.add(activeParent.key);
      return next;
    });
  }, [location.pathname, menu, isHrefActive]);

  const parentIsActive = (item: any) =>
    item.children?.some((child: any) => isHrefActive(child.href)) ?? false;

  const handleReferral = () => {
    setMenuOpen?.(false);
    navigate('/referral');
  };

  // 获取达人端订单信息统计
  const [orderStats, setOrderStats] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const { invitesId } = useInvitesStore();
  const getOrderStatus = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `/influencer/orderstats?hash=${invitesId}`
      );
      setOrderStats(res?.data?.data || {});
    } catch (error) {
      console.error('getOrderStatus error: ', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setOrderStats({});
      setLoading(false);
      return;
    }
    getOrderStatus();
  }, [isLoaded, isSignedIn]);

  const goToLogin = () => {
    window.location.href = buildLoginUrl();
  };

  const handleMenuClick = (item: any) => {
    if (guestMode) {
      setMenuOpen?.(false);
      if (item.key !== 'dashboard') goToLogin();
      return true;
    }
    if (item.key === 'leaderboard') {
      markLeaderboardSeen();
    }
    if (item.key === 'message') {
      setShowTimModal(true);
    }
    setMenuOpen?.(false);
    return false;
  };
  const shouldShowLeaderboardBadge = (item: any) =>
    item.key === 'leaderboard' && item.badge && showLeaderboardNewBadge;

  if (collapsed) {
    return (
      <div className="flex min-h-0 w-full flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col items-center gap-1 overflow-y-auto px-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {menu.map((item: any) => {
            const active =
              isHrefActive(item.href) ||
              (item.children?.length
                ? item.children.some((c: any) => isHrefActive(c.href))
                : false);
            const targetHref: string | undefined =
              item.href || item.children?.[0]?.href;

            if (item.key !== 'message' && !targetHref) return null;

            return (
              <SidebarNavItem
                key={item.key}
                label={item.label}
                icon={item.icon}
                collapsed
                selected={active}
                count={item.key === 'message' ? timUnreadCount : 0}
                ariaCurrent={active ? 'page' : undefined}
                cornerBadge={
                  shouldShowLeaderboardBadge(item) ? (
                    <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[var(--creator-sidebar-danger)]" />
                  ) : undefined
                }
                onClick={() => {
                  if (handleMenuClick(item)) return;
                  if (targetHref) navigate(targetHref);
                }}
              />
            );
          })}
        </div>
        {!guestMode && (
          <div className="flex w-full flex-shrink-0 flex-col items-center gap-3 px-3 pb-4 pt-3">
            {!isFromApp() && <DownloadAppTextButton compact />}
            <AvatarPanel setMenuOpen={setMenuOpen} compact />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex flex-col gap-1 px-3">
          {menu.map((item: any) => {
            const active = isHrefActive(item.href) || parentIsActive(item);
            const isOpen = openParentKeys.has(item.key);

            return (
              <React.Fragment key={item.key}>
                <SidebarNavItem
                  label={item.label}
                  icon={item.icon}
                  selected={active}
                  count={item.key === 'message' ? timUnreadCount : 0}
                  ariaCurrent={active ? 'page' : undefined}
                  ariaExpanded={item.children?.length ? isOpen : undefined}
                  labelBadge={
                    shouldShowLeaderboardBadge(item) ? (
                      <span className="rounded-full bg-[var(--creator-sidebar-danger)] px-1.5 py-0.5 font-['DM_Sans'] text-[10px] font-black leading-none text-white">
                        {item.badge}
                      </span>
                    ) : undefined
                  }
                  trailing={
                    item.children?.length ? (
                      <TopArrowIcon
                        className={`h-5 w-5 text-[var(--creator-sidebar-icon)] transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    ) : undefined
                  }
                  onClick={() => {
                    if (guestMode) {
                      handleMenuClick(item);
                      return;
                    }
                    if (item.children?.length) {
                      setOpenParentKeys((prev) => {
                        const next = new Set(prev);
                        if (next.has(item.key)) next.delete(item.key);
                        else next.add(item.key);
                        return next;
                      });
                      return;
                    }
                    if (item.href) {
                      setMenuOpen?.(false);
                      navigate(item.href);
                    }
                    handleMenuClick?.(item);
                  }}
                />

                {item.children?.length ? (
                  <div
                    className={`transition-all duration-200 ease-in-out overflow-hidden ${
                      isOpen ? 'max-h-[220px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="flex flex-col gap-[2px] pt-[2px]">
                      {item.children.map((child: any) => {
                        const childActive = isHrefActive(child.href);

                        return (
                          <button
                            type="button"
                            key={child.key}
                            className={`flex h-10 w-full items-center justify-between border-0 bg-transparent pl-10 pr-[10px] font-['Inter'] text-sm font-medium leading-5 hover:bg-[var(--creator-sidebar-surface)] ${
                              childActive
                                ? 'rounded-lg bg-[var(--creator-sidebar-selected)] text-[var(--creator-sidebar-text-strong)]'
                                : 'rounded-md text-[var(--creator-sidebar-text)]'
                            }`}
                            onClick={() => {
                              if (guestMode) {
                                goToLogin();
                                return;
                              }
                              if (child.href) {
                                setMenuOpen?.(false);
                                navigate(child.href);
                              }
                            }}
                            aria-current={childActive ? 'page' : undefined}
                          >
                            <span>{child.label}</span>
                            {child?.key !== 'completed' && (
                              <>
                                {loading && (
                                  <Skeleton.Node
                                    active
                                    style={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: '11px'
                                    }}
                                  />
                                )}
                                {!!orderStats?.[child?.key] && !loading && (
                                  <div className="px-2 h-[22px] flex items-center justify-center bg-[#FAFAF9] rounded-[11px] border border-[#E7E5E4]">
                                    <div className="text-center justify-start text-[#44403C] text-xs font-medium font-['DM_Sans'] leading-4">
                                      {orderStats?.[child?.key]}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className="flex flex-shrink-0 flex-col gap-2 bg-white px-4 pb-4">
        {(guestMode || referralInfo?.inviteEnabled) && (
          <button
            type="button"
            className="relative h-[100px] w-full overflow-hidden rounded-xl bg-[var(--creator-sidebar-surface)]"
            aria-label={
              guestMode
                ? 'Log in to get your invite link'
                : t('navbar.referral.get_my_invite_link', {
                    defaultValue: 'Get my invite link'
                  })
            }
            onClick={guestMode ? goToLogin : handleReferral}
          >
            <img
              src="https://website-resource.ahaglobal.io/ads-platform/creator/referral-menu-home.png"
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-[14px]">
              <img
                src="https://website-resource.ahaglobal.io/ads-platform/creator/invite-earn.png"
                alt="Invite and Earn"
                className="w-full"
              />
              <div className="text-center">
                <span className="font-['DM_Sans'] text-sm font-normal text-[#1C2024]">
                  {t('navbar.referral.earn_up_to', {
                    defaultValue: 'Refer creators and earn up to'
                  })}{' '}
                </span>
                <span className="font-['DM_Sans'] text-sm font-bold text-[#1C2024]">
                  US$
                </span>
                <span className="font-['DM_Sans'] text-sm font-black text-[#1C2024]">
                  10,000
                </span>
                <span className="font-['DM_Sans'] text-sm font-normal text-[#1C2024]">
                  .{' '}
                </span>
              </div>
            </div>
          </button>
        )}
        {!guestMode &&
          stepsDataReady &&
          stepsLists?.some((step) => !step.doneState) && (
            <div className="inline-flex w-full self-stretch flex-col items-start justify-start gap-3 rounded-xl bg-[var(--creator-sidebar-surface)] p-4">
              <div className="self-stretch inline-flex justify-between items-center">
                <div className="flex-1 font-['DM_Sans'] text-sm font-semibold leading-5 text-[#1C1917]">
                  {t(
                    'components.set_onboarding_prompt.four_steps_to_start_earning',
                    {
                      defaultValue: '4 steps to start earning'
                    }
                  )}{' '}
                </div>
                <div className="font-['Inter'] text-xs font-normal leading-[18px] text-[#A6A09B]">
                  {stepsLists?.filter((step) => step.doneState).length}/4
                </div>
              </div>
              <div
                className="h-1.5 w-full overflow-hidden rounded-[99px] bg-[#D6D3D1]"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={stepsLists.length}
                aria-valuenow={
                  stepsLists.filter((step) => step.doneState).length
                }
              >
                <div
                  className="h-full rounded-[99px] bg-[#FB6011]"
                  style={{
                    width: `${
                      (stepsLists.filter((step) => step.doneState).length /
                        stepsLists.length) *
                      100
                    }%`
                  }}
                />
              </div>
              <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                {stepsLists.map((step, index) => (
                  <button
                    type="button"
                    key={index}
                    className="inline-flex self-stretch items-center justify-start gap-1.5 border-0 bg-transparent p-0 text-left"
                    onClick={() => {
                      toStep(step.todoKey);
                      if (step?.todoKey !== 'takeTheTour') {
                        setMenuOpen?.(false);
                      }
                    }}
                  >
                    {step.doneState ? (
                      <div className="size-4 shrink-0 relative rounded-[999px] border border-[#009966] bg-[#009966] flex items-center justify-center">
                        <CheckedIcon className="w-3 h-3 text-white" />
                      </div>
                    ) : (
                      <div className="relative size-4 shrink-0 rounded-[999px] border border-[#D7D3D0]" />
                    )}
                    <div className="w-full flex justify-between items-center">
                      <div
                        className={`font-['Inter'] text-xs font-normal leading-[18px] ${
                          step.doneState
                            ? 'text-[#737373] line-through'
                            : 'text-[#292524]'
                        }`}
                      >
                        {index + 1}. {step.title}
                      </div>
                      {!step.doneState && (
                        <div className="font-['Inter'] text-xs font-normal leading-[18px] text-[#A6A09B]">
                          {step.costTime}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        {!isFromApp() && <DownloadAppTextButton />}
        <div>
          {guestMode ? (
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-xl border border-[#E7E5E4] p-3 text-left"
              onClick={goToLogin}
            >
              <img
                src="/logo192.png"
                alt=""
                className="h-10 w-10 rounded-full"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-[#292524]">
                  Guest preview
                </span>
                <span className="block truncate text-xs text-[#79716B]">
                  Log in to view your profile
                </span>
              </span>
              <span className="text-xs font-semibold text-[#FB6011]">
                Log in
              </span>
            </button>
          ) : (
            <AvatarPanel setMenuOpen={setMenuOpen} />
          )}
        </div>
        {!guestMode && (
          <ModalAfterRelease
            openModal={openModalAfterRelease}
            onCloseModal={() => setOpenModalAfterRelease(false)}
          />
        )}
      </div>
    </div>
  );
}
